// Message HTML (13-messages.md, "Formatting: the message HTML desyr accepts"): someone else's HTML,
// reduced to the allowed set and drawn as React, with links made safe, mxc pictures through the
// authenticated media, mention pills, spoilers and code blocks. Plain bodies get clickable links.
import { Picture } from '../blocks/Bits';
import type { MatrixClient, Room } from 'matrix-js-sdk';
import { createContext, createElement, Fragment, useContext, useState, type ReactNode } from 'react';
import { CopyButton, IconButton } from '../blocks/Button';
import { navigate } from '../core/router';
import { parseLink, roomPath } from '../core/routes';
import { t } from '../i18n';
import { findRoom, isJoined, pathOf } from '../session/rooms';
import { chatIcons } from './icons';
import { emojiByChar } from './emoji';
import { languageOf, useHighlight } from './prism';
import { nameIn } from './events';

/** What drawing a message needs to know: the room it is in, and what a person's pill does. */
export interface Place {
  client: MatrixClient;
  room: Room;
  onPerson?: (userId: string, anchor: HTMLElement) => void;
}
export const PlaceContext = createContext<Place | null>(null);
const usePlace = () => useContext(PlaceContext)!;

const KEEP = new Set(
  'font del h1 h2 h3 h4 h5 h6 blockquote p a ul ol sup sub li b i u strong em strike s code hr br div table thead tbody tr th td caption pre span img details summary'.split(' '),
);
const DROP = new Set(['style', 'script', 'textarea', 'option', 'noscript', 'mx-reply']);
const ATTRS: Record<string, string[]> = {
  font: ['color', 'data-mx-color', 'data-mx-bg-color'],
  span: ['data-mx-color', 'data-mx-bg-color', 'data-mx-spoiler', 'data-mx-maths', 'data-mx-pill', 'data-mx-ping'],
  div: ['data-mx-maths'],
  a: ['name', 'href'],
  img: ['width', 'height', 'alt', 'title', 'src', 'data-mx-emoticon'],
  ol: ['start', 'type'],
  code: ['class', 'data-desyr-label'],
};
const HEADING: Record<string, string> = { h1: 'h2', h2: 'h3', h3: 'h4', h4: 'h4', h5: 'h5', h6: 'h6' };
const SCHEMES = /^(https?|ftp|mailto|magnet):/i;
const COLOUR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const URLS = /\b(?:https?:\/\/|ftp:\/\/|mailto:|magnet:)[^\s<>"]*[^\s<>".,:;'")\]!?]/gi;
const EMOJI = /(\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic}|\p{Emoji_Modifier})*|\p{Regional_Indicator}{2})/gu;

/** 1 to 10 emoji (or :shortcodes:) and spaces only: drawn larger. */
export function emojiOnly(text: string): boolean {
  const left = text.replace(EMOJI, '').replace(/:[\w+-]+:/g, '').trim();
  const count = (text.match(EMOJI)?.length ?? 0) + (text.match(/:[\w+-]+:/g)?.length ?? 0);
  return !left && count > 0 && count <= 10;
}

const safeHref = (href: string | null) => (href && SCHEMES.test(href.trim()) && !href.trim().startsWith('//') ? href.trim() : undefined);

/** Text with emoji a little larger, and addresses as links (not inside links or code). */
function Text({ text, links }: { text: string; links: boolean }) {
  const parts: ReactNode[] = [];
  const pattern = links ? new RegExp(`${URLS.source}|${EMOJI.source}`, 'giu') : EMOJI;
  let last = 0;
  for (const m of text.matchAll(pattern)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const link = links && SCHEMES.test(m[0]);
    parts.push(link ? <Linked key={m.index} href={m[0]}>{m[0]}</Linked> : <span key={m.index} className="emoji" title={emojiByChar(m[0])?.label}>{m[0]}</span>);
    last = m.index + m[0].length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

/** A link: a mention pill when it names a person, channel or message; else opens in a new tab. */
function Linked({ href, children }: { href: string; children: ReactNode }) {
  const link = parseLink(href);
  // Pills need the room they're in; elsewhere (a topic window) a link stays a link.
  if (link && useContext(PlaceContext)) return <Pill link={link} own={href.includes('/to/#/')} text={typeof children === 'string' && children !== href ? children : undefined} />;
  return (
    <a href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  );
}

function Pill({ link, own, text }: { link: NonNullable<ReturnType<typeof parseLink>>; own: boolean; text?: string }) {
  const { client, room, onPerson } = usePlace();
  if (link.kind === 'user') {
    const name = nameIn(room, link.userId);
    const me = link.userId === client.getUserId();
    return (
      <span role="link" tabIndex={0} className={me ? 'pill me' : 'pill'} onClick={(e) => onPerson?.(link.userId, e.currentTarget)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onPerson?.(link.userId, e.currentTarget)}>
        @{name}
      </span>
    );
  }
  // A matrix.to link to a channel or message stays plain text (05).
  if (!own) return <>{text ?? link.room}</>;
  const target = findRoom(client, link.room);
  const named = target && isJoined(target) ? target.name : link.room.startsWith('#') ? link.room.slice(1).split(':')[0] : undefined;
  const channel = named ? `#${named}` : t('chat.aChannel');
  const label = text ?? (link.event ? t('chat.messageIn', { channel }) : channel);
  const open = () => navigate(target && isJoined(target) ? pathOf(client, target, link.event) : `${roomPath({}, link.room, link.event)}${link.via.length ? `?${link.via.map((v) => `viaServers=${encodeURIComponent(v)}`).join('&')}` : ''}`);
  return (
    <span role="link" tabIndex={0} className={target?.roomId === room.roomId ? 'pill me' : 'pill'} onClick={open} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && open()}>
      {label}
    </span>
  );
}

function Spoiler({ children }: { children: ReactNode }) {
  const [hidden, setHidden] = useState(true);
  return (
    <span role="button" tabIndex={0} aria-pressed={hidden} className={hidden ? 'spoiler hidden' : 'spoiler'} onClick={() => setHidden(false)} onKeyDown={() => setHidden(false)}>
      {children}
    </span>
  );
}

export function CodeBlock({ code, className, label }: { code: string; className?: string | null; label?: string | null }) {
  const lines = code.split('\n').length;
  const [open, setOpen] = useState(lines <= 14);
  const html = useHighlight(code, languageOf(className ?? null));
  return (
    <div className={open ? 'code-block' : 'code-block folded'}>
      {label && <div className="code-label">{label}</div>}
      <pre>{html ? <code className={className ?? undefined} dangerouslySetInnerHTML={{ __html: html }} /> : <code className={className ?? undefined}>{code}</code>}</pre>
      <div className="code-tools">
        {lines > 14 && <IconButton label={t(open ? 'chat.collapse' : 'chat.expand')} icon={open ? chatIcons.unfoldLess : chatIcons.unfoldMore} size={18} onClick={() => setOpen(!open)} />}
        <CopyButton text={code} small />
      </div>
    </div>
  );
}

function MxcImage({ el }: { el: Element }) {
  const { client } = usePlace();
  const src = el.getAttribute('src') ?? '';
  const alt = el.getAttribute('alt') ?? '';
  if (!src.startsWith('mxc://')) {
    const target = safeHref(src);
    return target ? <Linked href={target}>{alt || src}</Linked> : <>{alt}</>;
  }
  const url = client.mxcUrlToHttp(src, undefined, undefined, undefined, false, true, true) ?? undefined;
  const emoticon = el.hasAttribute('data-mx-emoticon');
  return <Picture className={emoticon ? 'emoticon' : 'inline-picture'} src={url} alt={alt} title={el.getAttribute('title') ?? undefined} width={emoticon ? undefined : Number(el.getAttribute('width')) || undefined} fallback={alt} />;
}

/** One sanitised node as React; `depth` stops at 100 levels. */
function draw(node: Node, key: number, depth: number, inCode = false, inLink = false): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return <Text key={key} text={node.textContent ?? ''} links={!inCode && !inLink} />;
  if (node.nodeType !== Node.ELEMENT_NODE || depth > 100) return null;
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (DROP.has(tag)) return null;
  const kids = (code = inCode, link = inLink) => [...el.childNodes].map((c, i) => draw(c, i, depth + 1, code, link));
  if (!KEEP.has(tag)) return <Fragment key={key}>{kids()}</Fragment>;
  const attr = (name: string) => (ATTRS[tag]?.includes(name) ? el.getAttribute(name) : null);
  switch (tag) {
    case 'a': {
      const target = safeHref(attr('href'));
      return target ? <Linked key={key} href={target}>{kids(inCode, true)}</Linked> : <Fragment key={key}>{kids()}</Fragment>;
    }
    case 'img':
      return <MxcImage key={key} el={el} />;
    case 'pre': {
      const code = el.querySelector('code');
      return <CodeBlock key={key} code={(code ?? el).textContent?.replace(/\n$/, '') ?? ''} className={code?.getAttribute('class')} label={code?.getAttribute('data-desyr-label')} />;
    }
    case 'code':
      return <code key={key}>{kids(true)}</code>;
    case 'span':
    case 'font': {
      const color = attr('data-mx-color') ?? attr('color');
      const bg = attr('data-mx-bg-color');
      const style = { color: color && COLOUR.test(color) ? color : undefined, background: bg && COLOUR.test(bg) ? bg : undefined };
      if (tag === 'span' && el.hasAttribute('data-mx-spoiler')) return <Spoiler key={key}>{kids()}</Spoiler>;
      return <span key={key} style={style}>{kids()}</span>;
    }
    case 'ol':
      return createElement('ol', { key, start: Number(attr('start')) || undefined, type: (attr('type') as 'a') ?? undefined }, kids());
    case 'br':
    case 'hr':
      return createElement(tag, { key });
    default:
      return createElement(HEADING[tag] ?? tag, { key }, kids());
  }
}

/** Formatted message HTML, sanitised and drawn. */
export function Html({ html }: { html: string }) {
  const body = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;
  return <>{[...body.childNodes].map((n, i) => draw(n, i, 0))}</>;
}

/** A plain body: line breaks and spaces kept, addresses clickable. */
export const Plain = ({ text }: { text: string }) => <Text text={text} links />;
